// src/pages/About/index.jsx
import React, { useState, useEffect } from 'react';
import { message, Button, Upload, Avatar } from 'antd';
import { UserOutlined, CameraOutlined, MailOutlined, CodeOutlined, HeartOutlined, GlobalOutlined, LinkOutlined, EditOutlined } from '@ant-design/icons';
import PostCard from '../../component/PostCard/index.jsx';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './index.css';

const About = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [recentPosts, setRecentPosts] = useState([]);
  const { isAuthenticated, user, getUserPosts, updateUserInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 如果用户已登录，使用登录用户的资料
    if (isAuthenticated && user) {
      setUserInfo(user);
      // 获取用户的文章（最近3篇）
      const userPosts = getUserPosts();
      const sortedPosts = [...userPosts]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);
      setRecentPosts(sortedPosts);
    }
  }, [isAuthenticated, user, getUserPosts]);

  const handleEditClick = () => {
    if (!isAuthenticated) {
      message.warning('请先登录以修改个人信息');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (!isAuthenticated) {
      message.warning('请先登录');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }
    
    // 更新用户信息
    const result = updateUserInfo(userInfo);
    if (result.success) {
      message.success('个人信息更新成功！');
    } else {
      message.error(result.message);
    }
    
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    if (isAuthenticated && user) {
      setUserInfo(user);
    }
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 处理头像上传
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return false;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过2MB!');
      return false;
    }
    
    return isImage && isLt2M;
  };

  const handleAvatarChange = ({ file }) => {
    if (file.status === 'done') {
      // 获取上传后的文件URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setUserInfo(prev => ({
          ...prev,
          avatar: e.target.result
        }));
      };
      reader.readAsDataURL(file.originFileObj);
    } else if (file.status === 'error') {
      message.error('头像上传失败');
    }
  };

  return (
    <div className="about-page">
      <div className="profile-container">
        {/* 头像上传区域 */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '2rem' }}>
          <Upload
            name="avatar"
            listType="picture-circle"
            className="avatar-uploader"
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={handleAvatarChange}
            disabled={!isEditing}
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                src={userInfo.avatar}
                style={{ 
                  border: '3px solid #e0f2fe',
                  cursor: isEditing ? 'pointer' : 'default',
                  transition: 'all 0.3s ease'
                }}
              />
              {isEditing && (
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(14, 165, 233, 0.7)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                  }}
                  className="avatar-overlay"
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0'}
                >
                  <CameraOutlined style={{ fontSize: '28px' }} />
                </div>
              )}
            </div>
          </Upload>
        </div>

        {/* 个人信息 */}
        <div className="profile-info">
          <h1>📝 关于 {userInfo.name || userInfo.username || '博主'}</h1>
          <p className="description">{userInfo.description || '欢迎来到我的个人博客空间！在这里分享我的思考、经验和见解。'}</p>
          <p className="welcome-message">📚 记录生活点滴，分享技术心得，交流学习体会</p>
        </div>

        {/* 信息列表 */}
        <div className="info-list">
          <div className="info-item">
            <span className="label">
              <MailOutlined style={{ marginRight: '8px', color: '#0ea5e9' }} />
              邮箱联系
            </span>
            {isEditing ? (
              <input
                type="email"
                name="email"
                value={userInfo.email || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="请输入邮箱地址"
                required
              />
            ) : (
              <span className="value">{userInfo.email || '未设置邮箱'}</span>
            )}
          </div>
          
          <div className="info-item">
            <span className="label">
              <CodeOutlined style={{ marginRight: '8px', color: '#10b981' }} />
              技术栈
            </span>
            {isEditing ? (
              <input
                type="text"
                name="skills"
                value={userInfo.skills || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="例如：React, JavaScript, Node.js"
              />
            ) : (
              <span className="value">{userInfo.skills || '未设置技术栈'}</span>
            )}
          </div>
          
          <div className="info-item">
            <span className="label">
              <HeartOutlined style={{ marginRight: '8px', color: '#f97316' }} />
              兴趣爱好
            </span>
            {isEditing ? (
              <input
                type="text"
                name="interests"
                value={userInfo.interests || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="例如：阅读, 旅行, 摄影"
              />
            ) : (
              <span className="value">{userInfo.interests || '未设置兴趣爱好'}</span>
            )}
          </div>
          
          <div className="info-item">
            <span className="label">
              <GlobalOutlined style={{ marginRight: '8px', color: '#8b5cf6' }} />
              GitHub 主页
            </span>
            {isEditing ? (
              <input
                type="url"
                name="github"
                value={userInfo.github || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="https://github.com/username"
              />
            ) : (
              <span className="value">
                {userInfo.github ? (
                  <a href={userInfo.github} target="_blank" rel="noopener noreferrer" style={{ color: '#67e8f9' }}>
                    {userInfo.github}
                  </a>
                ) : '未设置 GitHub 链接'}
              </span>
            )}
          </div>
          
          <div className="info-item">
            <span className="label">
              <LinkOutlined style={{ marginRight: '8px', color: '#0d9488' }} />
              个人网站
            </span>
            {isEditing ? (
              <input
                type="url"
                name="homepage"
                value={userInfo.homepage || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="https://yoursite.com"
              />
            ) : (
              <span className="value">
                {userInfo.homepage ? (
                  <a href={userInfo.homepage} target="_blank" rel="noopener noreferrer" style={{ color: '#67e8f9' }}>
                    {userInfo.homepage}
                  </a>
                ) : '未设置个人网站'}
              </span>
            )}
          </div>
          
          <div className="info-item">
            <span className="label">
              <EditOutlined style={{ marginRight: '8px', color: '#3b82f6' }} />
              个人简介
            </span>
            {isEditing ? (
              <textarea
                name="description"
                value={userInfo.description || ''}
                onChange={handleInputChange}
                className="edit-input"
                placeholder="介绍一下自己吧..."
                rows={4}
              />
            ) : (
              <span className="value">{userInfo.description || '博主还未添加个人简介'}</span>
            )}
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="action-buttons">
          {!isAuthenticated ? (
            <Button 
              className="edit-button" 
              onClick={() => navigate('/auth', { state: { from: location.pathname } })}
            >
              🔐 立即登录以编辑资料
            </Button>
          ) : !isEditing ? (
            <Button 
              className="edit-button" 
              onClick={handleEditClick}
            >
              ✏️ 编辑个人资料
            </Button>
          ) : (
            <div className="edit-actions">
              <Button 
                className="save-button" 
                onClick={handleSaveClick}
              >
                💾 保存更改
              </Button>
              <Button 
                className="cancel-button" 
                onClick={handleCancelClick}
              >
                🚫 取消编辑
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 用户文章 */}
      <div style={{ marginTop: '2.5rem', maxWidth: '800px', margin: '2.5rem auto', padding: '0 1rem' }}>
        <h2 style={{ textAlign: 'center', color: '#0c4a6e', marginBottom: '1.8rem', fontSize: '1.8rem' }}>
          {isAuthenticated ? '📝 我的最新文章' : '📚 作者文章'}
        </h2>
        {recentPosts && recentPosts.length > 0 ? (
          recentPosts.map(post => (
            <PostCard key={post.id} post={post} />
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#67e8f9', fontSize: '1.1rem' }}>
              {isAuthenticated 
                ? '😅 还没有发表过文章呢，快去<a href="/write" style="color: #0ea5e9; text-decoration: none;" onclick="location.href=\'/write\'">写一篇</a>吧！' 
                : '作者所有文章暂无显示'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default About;